import React, { useState } from 'react';

import { ENV } from '@environment';
import { Formik, FormikValues } from 'formik';
import * as yup from 'yup';

import Field from 'components/Field';
import TextArea from 'components/TextArea';
import useIsDesktop from 'hooks/useIsDesktop';
import useStoreTranslation from 'hooks/useStoreTranslation';
import ResponseScreen, { BackgroundTypes, IconTypes } from 'screens/components/ResponseScreen';
import { sendSupportEmail } from 'services/registrationService';
import { htmlT } from 'utils/generic';

import { Input } from '../../../styles';
import { ConfirmEmailValues } from '../../ConfirmEmail';
import { Container, Header, StyledForm, ButtonContainer, SendButton } from './styles';

interface Props {
  confirmEmail: ConfirmEmailValues | null;
}

interface Values {
  name: string;
  message: string;
}

const LANDING_URL = ENV.LANDING_PAGE as string;

const HelpMail = ({ confirmEmail }: Props) => {
  const { t } = useStoreTranslation();
  const isDesktop = useIsDesktop();
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  if (isDesktop) return null;

  const initialValues: Values = {
    name: '',
    message: '',
  };

  const validationSchema = yup.object().shape({
    name: yup.string().required(),
    message: yup.string().required(),
  });

  const handleSubmit = async (values: Values) => {
    if (!confirmEmail) return;

    setLoading(true);
    await sendSupportEmail({
      email: confirmEmail.email,
      subject: t('login.confirmEmail.help.mail.emailSubject'),
      ...values,
    });
    setSuccess(true);
    setLoading(false);
  };

  const handleGoHome = () => {
    window.location.href = LANDING_URL;
  };

  if (!confirmEmail) return null;

  return (
    <>
      <Container>
        <Header>{htmlT(t, 'login.confirmEmail.help.mail.title')}</Header>

        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
          isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
        >
          {({ isValid }: FormikValues) => (
            <StyledForm>
              <div>
                <Field name="name">
                  <Input
                    placeholder={t('login.confirmEmail.help.mail.namePlaceholder')}
                    autoCapitalize="off"
                    autoCorrect="off"
                  />
                </Field>

                <Field name="message" label={t('login.confirmEmail.help.mail.messageLabel')}>
                  <TextArea placeholder={t('login.confirmEmail.help.mail.messagePlaceholder')} />
                </Field>

                <ButtonContainer>
                  <SendButton disabled={!isValid || loading} loading={loading}>
                    {t('login.confirmEmail.help.mail.send')}
                  </SendButton>
                </ButtonContainer>
              </div>
            </StyledForm>
          )}
        </Formik>
      </Container>

      <ResponseScreen
        show={success}
        text={t('login.confirmEmail.help.mail.success.title')}
        subText={t('login.confirmEmail.help.mail.success.message', { mail: confirmEmail.email })}
        buttonText={t('login.confirmEmail.help.mail.success.continue')}
        buttonOnClick={handleGoHome}
        background={BackgroundTypes.Cloud}
        icon={IconTypes.PaperAirplane}
      />
    </>
  );
};

export default HelpMail;
